// Walkthrough — ported from V1 (public/app/index.html L16760–16893).
// Full-screen backdrop + centered modal. Role-based step arrays with content
// copied verbatim from V1. State writes go to opts via useOptsMutation:
//   • Close (✕ or backdrop)  → walkthroughSeenAt = ISO only (user can replay)
//   • Finish ✓               → walkthroughSeenAt = ISO + walkthroughCompleted = true
// Auto-navigates between routes using each step's `nav` target.

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOpts } from '../hooks/useOpts'
import { usePermissions } from '../hooks/usePermissions'
import { useOptsMutation, type OptsPatch } from '../hooks/useOptsMutation'
import type { Role } from '../types'

interface Step {
  title: string
  desc: string
  nav: string  // route path fragment — '' = overview (index)
}

// Map V1 `nav` ids to V2 router paths.
const NAV_PATH: Record<string, string> = {
  overview:  '/',
  projects:  '/projects',
  estimates: '/estimates',
  expenses:  '/expenses',
  payments:  '/payments',
  portfolio: '/portfolio',
  options:   '/options',
}

// ── Step content, copied verbatim from V1 ─────────────────────────────────

const WALK_ADMIN: Step[] = [
  { title: "Welcome, Admin!", desc: "You have full control — projects, financials, estimates, team management, and all settings. Let's take a quick tour.", nav: "overview" },
  {
    title: "Overview dashboard",
    desc: "Your home base. Active projects, upcoming work, outstanding payments, and today's tasks — all with color-coded status bars. Click any metric to jump directly there.",
    nav: "overview",
  },
  { title: "Projects", desc: "All projects with status dots and budget progress. Filter by On-going, Upcoming, On hold, or Complete with the clickable cards at the top.", nav: "projects" },
  {
    title: "Estimates",
    desc: "Build line-item estimates from templates, save as drafts, mark pending for approval, then convert to active projects. Generate branded PDFs and email them directly.",
    nav: "estimates",
  },
  {
    title: "Expenses",
    desc: "Log every expense with vendor, category, and payment method. Attach receipt photos. Set up recurring monthly expenses. Everything feeds into project budgets automatically.",
    nav: "expenses",
  },
  {
    title: "Payments In",
    desc: "Track client deposits, progress payments, and final payments. Generate branded PDF invoices and email them. Outstanding amounts highlight in amber.",
    nav: "payments",
  },
  {
    title: "Reports & Settings",
    desc: "P&L reports by month or by project. Manage your team with the team code. Configure themes, business profile, PDF options, and alert preferences.",
    nav: "options",
  },
]

const WALK_PM: Step[] = [
  { title: "Welcome, Project Manager!", desc: "You can view financials, manage schedules, track expenses, and oversee project progress. Here's your tour.", nav: "overview" },
  { title: "Overview", desc: "Active projects, today's tasks, and outstanding payments at a glance. Click any metric to navigate directly.", nav: "overview" },
  { title: "Projects", desc: "View all projects with budgets, status dots, and progress bars. Click into any project for line items, expenses, schedule, and photos.", nav: "projects" },
  { title: "Estimates & Expenses", desc: "View estimates, add expenses, track paid vs unpaid. You can create and manage expenses but cannot change team settings or roles.", nav: "estimates" },
  { title: "Schedule & Payments", desc: "Manage sub schedules inside each project. Track payment status. Generate invoices and email them to clients.", nav: "payments" },
]

const WALK_CREW: Step[] = [
  { title: "Welcome, Field Crew!", desc: "Your view focuses on what matters on-site — schedules and project status. Financial data is hidden from your view.", nav: "overview" },
  { title: "Overview", desc: "See your active projects and today's schedule. This is your daily check-in screen.", nav: "overview" },
  { title: "Projects", desc: "View project names, status, and client info. Open a project to see the Schedule tab and Photos tab — your main on-site tools.", nav: "projects" },
  {
    title: "That's it!",
    desc: "Your admin or PM handles the financial side. You focus on getting the work done. Check your schedule each morning and update project photos as you go.",
    nav: "overview",
  },
]

const WALK_INVESTOR: Step[] = [
  {
    title: "Built for investors.",
    desc: "Most construction apps are built for contractors billing clients. This one's built for you — the person who owns the property, manages the rehab, and needs to know if the deal actually makes money.",
    nav: "overview",
  },
  {
    title: "Your portfolio, one screen.",
    desc: "Every deal you're running — active flips, properties under contract, completed sales, and rentals you're holding. Capital in, profit out, ROI across everything. No spreadsheet can update itself like this.",
    nav: "portfolio",
  },
  {
    title: "The 70% rule, automatically.",
    desc: "Each deal card runs the numbers live. Green check means you bought right. Red X means you overpaid. You'll never have to punch numbers into a calculator at the closing table again.",
    nav: "portfolio",
  },
  {
    title: "Run the deal before you buy.",
    desc: "Open any property and hit the Deal tab. Plug in purchase price, ARV, rehab estimate, holding costs, selling costs — the profit updates instantly. Use this at the kitchen table before you make an offer.",
    nav: "projects",
  },
  {
    title: "Track every dollar of the rehab.",
    desc: "27 trade categories. Every expense you log subtracts from the budget automatically. You'll know exactly how much electrical ate into your flooring budget before the drywall goes up.",
    nav: "expenses",
  },
  {
    title: "Close the deal, lock in the profit.",
    desc: 'When the property sells, hit "Mark as sold." Enter the real sale price and fees. The app calculates your actual net profit — not an estimate, the real number. That deal moves to your Sold portfolio forever.',
    nav: "portfolio",
  },
  {
    title: "Or hold it. We handle that too.",
    desc: "BRRRR strategy? Convert a finished rehab into a rental. Enter the monthly rent and refi details. The whole project transforms — rehab data archives, and you get cash flow tracking, cap rate, and equity calculations.",
    nav: "projects",
  },
  {
    title: "Landlord mode, built in.",
    desc: "Once it's a rental, track every maintenance call, repair bill, and property management fee. 14 categories. The app calculates your monthly cash flow after all expenses — mortgage, insurance, taxes, and every leaky faucet.",
    nav: "projects",
  },
  {
    title: "No invoicing yourself.",
    desc: "When you're the owner, the app knows. Payment tracking disappears — no fake invoices, no \"outstanding balance\" to yourself. Just budget, spent, and what's left. Clean.",
    nav: "projects",
  },
  {
    title: "Go make money.",
    desc: "Portfolio tracking, deal analysis, rehab management, exit strategies, rental cash flow — all in your pocket. Start adding your first property.",
    nav: "overview",
  },
]

const WALKS: Record<Role, Step[]> = {
  admin: WALK_ADMIN,
  project_manager: WALK_PM,
  field_crew: WALK_CREW,
}

function pickSteps(role: Role, companyType: string | undefined): Step[] {
  if (companyType === 'investor') return WALK_INVESTOR
  return WALKS[role] ?? WALK_CREW
}

interface Props {
  open: boolean
  onClose: () => void
}

export default function Walkthrough({ open, onClose }: Props) {
  const opts = useOpts()
  const { role } = usePermissions()
  const navigate = useNavigate()
  const optsMut = useOptsMutation()

  const companyType = (opts as Record<string, unknown>).companyType as string | undefined
  const [steps] = useState<Step[]>(() => pickSteps(role, companyType))
  const [idx, setIdx] = useState(0)

  const step = steps[idx]

  // Navigate to the step's target route when the step changes.
  useEffect(() => {
    if (!open || !step) return
    const path = NAV_PATH[step.nav] ?? '/'
    navigate(path)
  }, [open, step, navigate])

  if (!open || !step) return null

  function writeSeen(completed: boolean) {
    const seen = (opts as Record<string, unknown>).walkthroughSeenAt as string | undefined
    const patch: OptsPatch = {
      walkthroughSeenAt: seen || new Date().toISOString(),
    } as OptsPatch
    if (completed) (patch as Record<string, unknown>).walkthroughCompleted = true
    optsMut.mutate(patch)
  }

  function close() {
    // ✕ or backdrop — stamp seen but don't mark complete, so the user can replay later.
    writeSeen(false)
    onClose()
  }

  function finish() {
    writeSeen(true)
    onClose()
  }

  function next() {
    if (idx >= steps.length - 1) {
      finish()
    } else {
      setIdx(idx + 1)
    }
  }

  function back() {
    if (idx > 0) setIdx(idx - 1)
  }

  const isLast = idx >= steps.length - 1

  return (
    <>
      {/* Dark backdrop */}
      <div
        onClick={close}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,.55)',
          zIndex: 9998,
        }}
      />
      {/* Centered modal */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="walkthrough-title"
        style={{
          position: 'fixed',
          zIndex: 9999,
          background: 'var(--surface)',
          border: '1px solid var(--accent)',
          borderRadius: 12,
          padding: '20px 24px',
          maxWidth: 360,
          width: 'calc(100vw - 2rem)',
          boxShadow: '0 12px 40px rgba(0,0,0,.4)',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%,-50%)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{
            fontSize: 10,
            color: 'var(--accent)',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '.06em',
          }}>
            Step {idx + 1} of {steps.length}
          </div>
          <button
            onClick={close}
            aria-label="Close walkthrough"
            style={{
              border: 'none',
              background: 'transparent',
              color: 'var(--text-faint)',
              cursor: 'pointer',
              fontSize: 16,
              padding: 4,
            }}
          >
            ✕
          </button>
        </div>
        <div
          id="walkthrough-title"
          style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, letterSpacing: '-.02em' }}
        >
          {step.title}
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 16 }}>
          {step.desc}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
          {idx > 0 ? (
            <button
              onClick={back}
              style={{
                padding: '8px 16px',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius)',
                background: 'var(--surface)',
                color: 'var(--text-muted)',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              ← Back
            </button>
          ) : (
            <div />
          )}
          <button
            onClick={next}
            style={{
              padding: '8px 20px',
              border: 'none',
              borderRadius: 'var(--radius)',
              background: 'var(--accent)',
              color: '#fff',
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: 13,
            }}
          >
            {isLast ? 'Finish ✓' : 'Next →'}
          </button>
        </div>
      </div>
    </>
  )
}
